import React from "react";
import { Link } from "react-router-dom";
import Seo from "./Seo";

const playbooks = [
  {
    id: "incident-triage",
    title: "Incident Triage",
    tags: ["SOC", "IR"],
    context:
      "Initial assessment and containment flow for web application incidents.",
    signals: [
      "Multiple 5xx spikes",
      "WAF alerts (SQLi/XSS)",
      "Unusual auth failures",
    ],
    steps: [
      "Triage alerts and correlate with logs",
      "Identify scope and affected assets",
      "Apply temporary containment (WAF block rules, revoke tokens)",
    ],
    validation: [
      "No new matching alerts within 30 minutes",
      "Healthy latency and error budgets",
    ],
    automation: [
      "Auto-block offending IP ranges for 24h",
      "Ticket creation with context (SIEM integration)",
    ],
  },
  {
    id: "k8s-baseline-hardening",
    title: "Kubernetes Baseline Hardening",
    tags: ["Kubernetes", "DevSecOps"],
    context: "Secure defaults for small-to-mid clusters.",
    signals: ["Audit log findings", "Privileged pods detected"],
    steps: [
      "Enforce PodSecurity standards (baseline/restricted)",
      "NetworkPolicies for namespace isolation",
      "Restrict image sources and enable scanning",
    ],
    validation: ["No privileged pods", "Ingress/Egress restricted by policy"],
    automation: ["Gate in CI for image signatures", "OPA/Gatekeeper templates"],
  },
  {
    id: "pipeline-security-checks",
    title: "Pipeline Security Checks",
    tags: ["CI/CD", "SupplyChain"],
    context: "Baseline checks to prevent risky artifacts reaching prod.",
    signals: ["Unsigned artifacts", "High severity vulnerabilities"],
    steps: [
      "SCA (dependency) scan and SAST on PRs",
      "Sign artifacts (cosign)",
      "Block deploy on critical vulnerabilities",
    ],
    validation: ["All artifacts signed", "Vuln gates enforced"],
    automation: ["GitHub Actions reusable workflows", "Slack notifications"],
  },
];

const tagColors = {
  SOC: { bg: "rgba(239, 68, 68, 0.1)", text: "#dc2626" },
  IR: { bg: "rgba(239, 68, 68, 0.1)", text: "#dc2626" },
  Kubernetes: { bg: "rgba(59, 130, 246, 0.1)", text: "#2563eb" },
  DevSecOps: { bg: "rgba(34, 197, 94, 0.1)", text: "#16a34a" },
  "CI/CD": { bg: "rgba(168, 85, 247, 0.1)", text: "#9333ea" },
  SupplyChain: { bg: "rgba(251, 146, 60, 0.1)", text: "#ea580c" },
};

export default function Playbooks() {
  return (
    <div className="playbooks-container">
      <Seo
        title="Playbooks - LinuxSecOps"
        description="SOC and DevSecOps playbooks with structured steps: Context, Signals, Steps, Validation, Automation."
        path="/playbooks"
      />

      {/* Hero Section */}
      <section className="playbooks-hero">
        <div className="playbooks-hero-content">
          <div className="hero-badge">Playbooks</div>
          <h1 className="playbooks-hero-title">
            Security Runbooks
            <span className="playbooks-hero-accent"> & Procedures</span>
          </h1>
          <p className="playbooks-hero-description">
            Structured playbooks for SOC operations and DevSecOps workflows.
            Each playbook includes context, signals, steps, validation, and automation hooks.
          </p>
        </div>
      </section>

      {/* Playbooks */}
      <div className="playbooks-content">
        <div className="playbooks-grid">
          {playbooks.map((p) => (
            <div key={p.id} className="playbook-card">
              <div className="playbook-header">
                <h2 className="playbook-title">{p.title}</h2>
                <div className="playbook-tags">
                  {p.tags.map((t) => (
                    <span
                      key={t}
                      className="playbook-tag"
                      style={{
                        background: tagColors[t]?.bg || "#f1f5f9",
                        color: tagColors[t]?.text || "#475569",
                      }}
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
              <p className="playbook-context">{p.context}</p>

              <div className="playbook-sections">
                <div className="playbook-section">
                  <h4 className="playbook-section-title">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="8" x2="12" y2="12"/>
                      <line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    Signals
                  </h4>
                  <ul>
                    {p.signals.map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ul>
                </div>

                <div className="playbook-section">
                  <h4 className="playbook-section-title">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="8" y1="6" x2="21" y2="6"/>
                      <line x1="8" y1="12" x2="21" y2="12"/>
                      <line x1="8" y1="18" x2="21" y2="18"/>
                      <line x1="3" y1="6" x2="3.01" y2="6"/>
                      <line x1="3" y1="12" x2="3.01" y2="12"/>
                      <line x1="3" y1="18" x2="3.01" y2="18"/>
                    </svg>
                    Steps
                  </h4>
                  <ol>
                    {p.steps.map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ol>
                </div>

                <div className="playbook-section">
                  <h4 className="playbook-section-title">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    Validation
                  </h4>
                  <ul>
                    {p.validation.map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ul>
                </div>

                <div className="playbook-section">
                  <h4 className="playbook-section-title">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                    </svg>
                    Automation
                  </h4>
                  <ul>
                    {p.automation.map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="playbooks-cta">
          <p>Need custom playbooks for your organization?</p>
          <Link to="/services" className="btn-primary">
            View Services
          </Link>
        </div>
      </div>
    </div>
  );
}
